import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Typography,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React from 'react';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'warning' | 'error' | 'info' | 'success';
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  showCloseButton?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  confirmColor = 'primary',
  showCloseButton = true,
}) => {
  const theme = useTheme();

  const variantConfig = {
    warning: {
      icon: WarningIcon,
      color: theme.palette.primary.light,
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.lighter,
    },
    error: {
      icon: ErrorIcon,
      color: '#f44336',
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
    },
    info: {
      icon: InfoIcon,
      color: '#2196f3',
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
    },
    success: {
      icon: CheckCircleIcon,
      color: '#4caf50',
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
    },
  };

  const config = variantConfig[variant];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      slots={{ transition: Transition }}
      slotProps={{
        transition: { timeout: 300 },
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            overflow: 'visible',
            m: 2,
          },
        },
      }}
      maxWidth="xs"
      fullWidth
    >
      <Box
        sx={(theme) => ({
          position: 'relative',
          background: config.backgroundColor,
          borderRadius: '8px 8px 0 0',
          p: 2,
          textAlign: 'center',
        })}
      >
        {showCloseButton && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              fontSize: '1.25rem',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {title}
          </DialogTitle>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 2, pt: 1.5, textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.5,
            fontSize: '0.875rem',
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 2,
          pt: 1,
          gap: 1,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderRadius: 1.5,
            px: 2,
            py: 0.5,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            borderColor: 'grey.300',
            color: 'grey.700',
            '&:hover': {
              borderColor: 'grey.400',
              backgroundColor: 'grey.50',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          size="small"
          sx={{
            borderRadius: 1.5,
            px: 2,
            py: 0.5,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
